import {FormEvent, useState, useRef} from "react"
import {Trans, useTranslation} from "react-i18next"
import {Box, Button, Typography, Link} from "@mui/material"
import {Panel, PanelDivider} from "@indebted/components/Panel"
import {styled} from "@mui/material/styles"
import {SetupIntent, type StripeElementLocale} from "@stripe/stripe-js"
import {
	PaymentMethodForm,
	ConfirmationToken,
	PaymentMethodPreview,
} from "@indebted/stripe-payment-arrangement/PaymentMethodForm"
import {useNotification} from "@indebted/components/Notification"
import {useNavigate} from "react-router-dom"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"

import {nextPage} from "../Navigator"
import {ALLOWED_PAYMENTMETHOD_CARD_FUNDING} from "../../../env"
import {ValidLocale} from "../../../src/Locales"
import {AssertUnreachable} from "../assertUnreachable"
import {DateButton} from "../Slider/DateButton"

import {ESignConsentIfNeeded} from "./ESignConsentIfNeeded"

interface PaymentFormProps {
	ClientSecret: string
	CustomerCountryISOAlpha: string
	RequestESIGNConsent: boolean
	RequiresEFTADisclosure: boolean
	PublishableKey: string
	SecureCode: string
	CustomerID: string
	EFTAPage: string
	DisableButton?: boolean
	showStripeTerms: boolean
	ButtonLabel: string
	onSuccess: ({PaymentMethodID, ProviderRawData}: {PaymentMethodID: string; ProviderRawData: unknown}) => void
}

type WithDatePicker = {
	ShowDatePicker: true
	date: Date
	maxDate: Date
	onDateChange: (date: Date) => void
}

type WithoutDatePicker = {
	ShowDatePicker: false
}

type DatePickerConfig = WithDatePicker | WithoutDatePicker

export function PaymentForm({
	ClientSecret,
	CustomerCountryISOAlpha,
	RequestESIGNConsent,
	RequiresEFTADisclosure,
	PublishableKey,
	SecureCode,
	CustomerID,
	EFTAPage,
	onSuccess,
	DisableButton = false,
	showStripeTerms,
	ButtonLabel,
	...props
}: PaymentFormProps & DatePickerConfig) {
	const {t, i18n} = useTranslation()
	const {notification} = useNotification()
	const navigate = useNavigate()
	const stripeLocale = localeToStripeLocale(i18n.language as ValidLocale)

	const [submitting, setSubmitting] = useState(false)
	const [form, setForm] = useState({Email: "", Name: ""})

	const esignConsentRef = useRef<{validate: () => boolean; submit: () => Promise<void>}>(null)
	const stripeRef = useRef<{
		createConfirmationToken: (form: {Name: string; Email: string}) => Promise<ConfirmationToken>
		confirmSetup: (confirmationTokenID: string) => Promise<{setupIntent: SetupIntent}>
	}>(null)

	const handleInput = (name: string, value: string) => {
		setForm({...form, [name]: value})
	}

	const onError = (error: unknown) => {
		if (error instanceof Error && error.message) {
			notification.error(error.message, 5000)
		} else {
			notification.error(t("unexpected_error.message"), 5000)
		}
	}

	const onSuccessPaymentCreation = async (response: {setupIntent: SetupIntent}) => {
		onSuccess({
			PaymentMethodID: response.setupIntent.payment_method as string,
			ProviderRawData: response,
		})
	}

	const redirectToEFTA = (confirmationToken: {id: string}) => {
		const searchParams = new URLSearchParams({
			clientSecret: ClientSecret,
			token: confirmationToken.id,
		})

		navigate(`${EFTAPage}?${searchParams.toString()}`)
	}

	const validateFundingType = (result: PaymentMethodPreview): boolean => {
		const allowedFundingTypesStr = ALLOWED_PAYMENTMETHOD_CARD_FUNDING
		const allowedFundingTypes = allowedFundingTypesStr
			? allowedFundingTypesStr.split(",").map((type) => type.trim())
			: ["debit", "prepaid"]

		if (!allowedFundingTypes.includes(result.card?.funding ?? "")) {
			return false
		}

		return true
	}

	const submit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		// See https://stripe.com/docs/stripe-js/react#useelements-hook
		if (!stripeRef.current || !esignConsentRef.current) {
			return
		}

		const emailValidationRegex =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

		if (form.Name.trim() == "" || !form.Email.match(emailValidationRegex)) {
			onError(new Error(t("payment_arrangement_payment_form_page.form.errors.email_name_validation")))
			return
		}

		if (!esignConsentRef.current.validate()) {
			return
		}

		setSubmitting(true)
		try {
			await esignConsentRef.current.submit()

			const confirmationToken = await stripeRef.current.createConfirmationToken(form)

			if (
				confirmationToken.payment_method_preview.type === "card" &&
				!validateFundingType(confirmationToken.payment_method_preview)
			) {
				throw new Error(t("payment_arrangement_payment_form_page.form.errors.funding_type"))
			}

			if (RequiresEFTADisclosure) {
				setSubmitting(false)
				redirectToEFTA(confirmationToken)
				return
			}

			const confirmResult = await stripeRef.current.confirmSetup(confirmationToken.id)

			const response = {
				paymentMethod: confirmationToken.payment_method_preview,
				setupIntent: confirmResult.setupIntent,
			}

			await onSuccessPaymentCreation(response)
		} catch (error) {
			console.error(error)
			onError(error)
			setSubmitting(false)
		}
	}

	return (
		<form onSubmit={submit}>
			<Panel sx={{marginTop: "2rem"}}>
				<Box display="flex" flexDirection="column" alignItems="stretch">
					<Box display="flex" flexDirection="column" alignItems="stretch" gap={5}>
						<Box>
							<Label htmlFor="fullname">
								{t("payment_arrangement_payment_form_page.form.fullname.label")}
							</Label>
							<Input
								id="fullname"
								name="fullname"
								type="text"
								autoComplete="name"
								placeholder={t("payment_arrangement_payment_form_page.form.fullname.label")}
								value={form.Name}
								onChange={(e) => handleInput("Name", e.target.value)}
							/>
						</Box>

						<Box>
							<Label htmlFor="email">{t("payment_arrangement_payment_form_page.form.email.label")}</Label>
							<Input
								id="email"
								autoComplete="email"
								value={form.Email}
								placeholder={t("payment_arrangement_payment_form_page.form.email.label")}
								onChange={(e) => handleInput("Email", e.target.value)}
							/>
							<Typography
								variant="caption"
								color="textSecondary"
								component="p"
								textAlign="start"
								marginInlineStart="0.5rem"
								fontSize="0.6875rem"
								marginTop="0.25rem"
							>
								{t("payment_arrangement_payment_form_page.form.email.help")}
							</Typography>
						</Box>
					</Box>
					<PanelDivider />
					<Box>
						<PaymentMethodForm
							ref={stripeRef}
							clientSecret={ClientSecret}
							CustomerCountryISOAlpha={CustomerCountryISOAlpha}
							stripeLocale={stripeLocale}
							PublishableKey={PublishableKey}
							showStripeTerms={showStripeTerms}
						/>
						<ESignConsentIfNeeded
							ref={esignConsentRef}
							RequestESIGNConsent={RequestESIGNConsent}
							SecureCode={SecureCode}
							CustomerID={CustomerID}
						/>
					</Box>
				</Box>
				<PanelDivider />
				{props.ShowDatePicker && (
					<Box marginBottom="1.5rem">
						<Label>{t("payment_arrangement.payment_method.payment_date")}</Label>
						<DateButton startDate={props.date} maxDate={props.maxDate} onDateChanged={props.onDateChange} />
					</Box>
				)}
				<Box display="flex" flexDirection="column" alignItems="stretch" gap={5}>
					<Button fullWidth type="submit" disabled={submitting || DisableButton}>
						{ButtonLabel}
					</Button>
					<Typography variant="caption" color="textSecondary" component="p" align="center">
						<Trans
							i18nKey="payment_arrangement_payment_form_page.terms_and_conditions.text"
							values={{ButtonLabel}}
							components={{a: <Link />}}
						/>
					</Typography>
				</Box>
			</Panel>
		</form>
	)
}

const Label = styled("label")`
	color: #737373;
	font-size: 0.875rem;
	font-style: normal;
	font-weight: 500;
	line-height: 1.25rem; /* 142.857% */
	letter-spacing: 0rem;
	display: block;
	padding-bottom: 0.5rem;
`

const Input = styled("input")`
	display: flex;
	padding: 0.75rem;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.25rem;
	align-self: stretch;
	border-radius: 0.5rem;
	border: 1px solid #e5e5e5;
	background: #f6f6f6;
	// box-shadow: 0px 4px 24px 0px rgba(54, 59, 100, 0.2);
	font-size: 1rem;

	width: 100%;

	&::placeholder {
		color: #737373;

		font-size: 1rem;
		font-style: normal;
		font-weight: 400;
		line-height: 1.25rem;
		letter-spacing: 0rem;
	}
`

function localeToStripeLocale(Locale: ValidLocale): StripeElementLocale {
	switch (Locale) {
		case "en-US":
			return "en"
		case "en-AE":
		case "en-GB":
			return "en-GB"
		case "en-CA":
			return "en-CA"
		case "en-AU":
			return "en-AU"
		case "en-NZ":
			return "en-NZ"
		case "es-MX":
		case "es-US":
			return "es"
		case "fr-CA":
			return "fr-CA"
		case "ar-AE":
			return "ar"
		case "pt-BR":
			return "pt-BR"
		default:
			AssertUnreachable(Locale, "Stripe doesn't support locale")
	}
}
